<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
} from "../../composables/useSeo";
import { usePhoneNumber } from "../../composables/usePhoneNumber";
import { useAnalytics } from "../../composables/useAnalytics";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();
const { servicePrice } = usePricing();

useSeoBase({
  title: "Services | Junk Guys MN",
  description:
    "Professional junk removal and light demolition services across the Twin Cities. Same-day service available.",
  canonical: "https://junkguysmn.com/services",
});

useJsonLd([
  orgNode(),
  localBusinessNode(),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "Services", url: "https://junkguysmn.com/services" },
  ]),
]);

// Service data with descriptions, pricing, and icons
const baseServices = [
  {
    id: "junk-removal",
    title: "Junk Removal",
    icon: "🗑️",
    description:
      "Full-service hauling & disposal for any type of household or commercial junk.",
    features: [
      "Same-day service available",
      "Volume-based pricing",
      "Responsible disposal",
    ],
    pricing: "$200 minimum",
    timeEstimate: "1-3 hours",
    examples: [
      "Furniture",
      "Electronics",
      "Clothing",
      "Books",
      "Toys",
      "Kitchen items",
    ],
  },
  {
    id: "appliance-removal",
    title: "Appliance Removal",
    icon: "🔌",
    description: "Safe removal and disposal of all types of appliances.",
    features: ["Disconnect ready", "Safe handling", "Proper disposal"],
    pricing: "Starting at $150",
    timeEstimate: "30 min - 1 hour",
    examples: [
      "Refrigerators",
      "Washing machines",
      "Dryers",
      "Stoves",
      "Dishwashers",
    ],
  },
  {
    id: "furniture-removal",
    title: "Furniture Removal",
    icon: "🪑",
    description: "Professional furniture removal with careful handling.",
    features: ["Careful handling", "Same-day pickup", "Donation when possible"],
    pricing: "$200 minimum",
    timeEstimate: "1-2 hours",
    examples: ["Couches", "Beds", "Dressers", "Tables", "Office furniture"],
  },
  {
    id: "construction-debris",
    title: "Construction Debris",
    icon: "🏗️",
    description: "Removal of construction waste and renovation debris.",
    features: [
      "Non-hazardous materials",
      "Heavy debris handling",
      "Site cleanup",
    ],
    pricing: "Starting at $250",
    timeEstimate: "2-4 hours",
    examples: [
      "Drywall",
      "Wood scraps",
      "Tile pieces",
      "Insulation",
      "Plumbing parts",
    ],
  },
  {
    id: "garage-cleanout",
    title: "Garage Cleanout",
    icon: "🚗",
    description: "Complete garage organization and junk removal service.",
    features: [
      "Full organization",
      "Sorting assistance",
      "Donation coordination",
    ],
    pricing: "$200 minimum",
    timeEstimate: "2-6 hours",
    examples: [
      "Tools",
      "Sports equipment",
      "Storage items",
      "Old furniture",
      "Seasonal items",
    ],
  },
  {
    id: "shed-demolition",
    title: "Shed Demolition",
    icon: "🏠",
    description: "Complete shed removal and site cleanup.",
    features: ["Full demolition", "Site cleanup", "Debris removal"],
    pricing: "Starting at $650",
    timeEstimate: "2-6 hours",
    examples: [
      "Garden sheds",
      "Storage sheds",
      "Tool sheds",
      "Playhouses",
      "Greenhouses",
    ],
  },
  {
    id: "deck-removal",
    title: "Deck Removal",
    icon: "🪜",
    description: "Professional deck demolition and disposal.",
    features: ["Complete removal", "Site preparation", "Heavy equipment"],
    pricing: "On-site quote",
    timeEstimate: "3-8 hours",
    examples: ["Wood decks", "Composite decks", "Railings", "Stairs", "Posts"],
  },
  {
    id: "hot-tub-removal",
    title: "Hot Tub Removal",
    icon: "🛁",
    description: "Specialized hot tub removal and disposal service.",
    features: ["Safe cutting", "Electrical disconnect", "Access permitting"],
    pricing: "$450 - $750",
    timeEstimate: "2-4 hours",
    examples: [
      "Acrylic hot tubs",
      "Fiberglass tubs",
      "Wooden tubs",
      "Cover removal",
    ],
  },
];

const services = computed(() =>
  baseServices.map((service) => ({
    ...service,
    pricing: servicePrice(service.id).value,
  })),
);
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">Our Services</h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Full-service junk removal and light demolition across the Twin
            Cities. Same-day service available for urgent needs.
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Grid -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">What We Handle</h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            From small appliance removal to complete garage cleanouts, we handle
            every job with professionalism and care.
          </p>
        </div>

        <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="service in services"
            :key="service.id"
            class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
          >
            <!-- Service Header -->
            <div class="p-6 border-b border-slate-100">
              <div class="flex items-center gap-4 mb-4">
                <div class="text-4xl">{{ service.icon }}</div>
                <div>
                  <h3 class="text-xl font-bold text-slate-900">
                    {{ service.title }}
                  </h3>
                  <p class="text-sm text-slate-600">
                    {{ service.description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Service Details -->
            <div class="p-6">
              <!-- Pricing & Time -->
              <div class="grid grid-cols-2 gap-4 mb-6">
                <div class="text-center p-3 bg-primary-main/5 rounded-lg">
                  <div class="text-lg font-bold text-primary-main">
                    {{ service.pricing }}
                  </div>
                  <div class="text-xs text-primary-main">Starting Price</div>
                </div>
                <div class="text-center p-3 bg-primary-light/10 rounded-lg">
                  <div class="text-lg font-bold text-primary-light">
                    {{ service.timeEstimate }}
                  </div>
                  <div class="text-xs text-primary-light">Typical Duration</div>
                </div>
              </div>

              <!-- Features -->
              <div class="mb-6">
                <h4 class="text-sm font-semibold text-slate-700 mb-3">
                  Key Features
                </h4>
                <ul class="space-y-2">
                  <li
                    v-for="feature in service.features"
                    :key="feature"
                    class="flex items-center gap-2 text-sm text-slate-600"
                  >
                    <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                    {{ feature }}
                  </li>
                </ul>
              </div>

              <!-- Examples -->
              <div class="mb-6">
                <h4 class="text-sm font-semibold text-slate-700 mb-3">
                  Examples
                </h4>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="example in service.examples"
                    :key="example"
                    class="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                  >
                    {{ example }}
                  </span>
                </div>
              </div>

              <!-- CTA -->
              <div class="flex gap-3">
                <NuxtLink
                  :to="`/services/${service.id}`"
                  class="flex-1 bg-primary-main hover:bg-primary-dark text-white text-center py-2 px-4 rounded-lg text-sm font-medium transition-colors"
                >
                  Learn More
                </NuxtLink>
                <a
                  :href="phoneNumberTel"
                  @click="trackConversion"
                  class="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-center py-2 px-4 rounded-lg text-sm font-medium transition-colors"
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">How It Works</h2>
          <p class="text-lg text-slate-600">
            Simple, transparent process from start to finish
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">1</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Get a Quote
            </h3>
            <p class="text-slate-600">
              Text us a photo or book an on-site quote. We'll give you a fair,
              transparent price.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">2</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              We Handle Everything
            </h3>
            <p class="text-slate-600">
              Our crew arrives on time, carefully removes your items, and leaves
              your space clean.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">3</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Responsible Disposal
            </h3>
            <p class="text-slate-600">
              We donate usable items and recycle when possible. Only true waste
              goes to the landfill.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-7xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Get Started?
        </h2>
        <p class="text-xl text-white mb-8 max-w-2xl mx-auto">
          Get your junk removal done today. Fast, reliable, and eco-friendly
          service across the Twin Cities.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            @click="trackConversion"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
